import React, { useCallback, useEffect, useState} from 'react';

import {SafeAreaView} from 'react-native-safe-area-context';
import { useFocusEffect, useNavigation, useRoute} from '@react-navigation/native';
import {useTheme} from 'styled-components/native';
import {RFValue} from 'react-native-responsive-fontsize';
import analytics from '@react-native-firebase/analytics';
import {trackEvent} from '../../lib/analytics';

import {Button} from '../../components/Interface/Forms/Buttons/Button';
import {Header} from '../../components/Interface/Headers/Header';

import {
  Container,
  FuelImage,
  IconContainer,
  Info,
  Title,
  Content,
  SectionTitle,
  Section,
  SectionHeader,
  QuantityRow,
  List,
  Price,
  PriceInfo,
  QuantityContainer,
  QuantityButton,
  QuantityIcon,
  Quantity,
  Footer,
  SectionDescription,
  ProductInfos,
  LinkButtonContainer,
  StationContainer,
  StationInfosContainer,
  StationImage,
  StationInfos,
  StationTitle,
  StationDescription,
  StationMapIcon,
  AlertContainer,
} from './styles';

import {StepList} from '../../components/Interface/List/StepList';
import {useAuth} from '../../context/auth';

import {Badge} from '../../components/Interface/Badges/Badge';
import {LinkButton} from '../../components/Interface/Forms/Buttons/LinkButton';
import {RootStackParamList} from '../../dtos/RoutesDTO';
import {api} from '../../services/api';
import {rastrear, STATUS_BY_TIPO_PRODUTO} from '../../services/rastrear';
import {StationDTO} from '../../dtos/StationDTO';
import {StationsModal} from '../../components/shared/Stations/StationsModal';

export function Product() {
  const theme = useTheme();
  const navigation = useNavigation();
  const route = useRoute();
  const {isLogged, openSign} = useAuth();



  const {
    id,
    name,
    price,
    alerta,
    imageUrl,
    unidade,
    localidade,
    coupon,
    posto,
  } = route.params as RootStackParamList['Product'];

  const priceUnformatted = price.replace('R$', '').replace('.', '').replace(',', '.');

  const fuelPrice = parseFloat(priceUnformatted);

  const [stationsByProductId, setStationsByProductId] = useState<StationDTO[]>(
    [] as StationDTO[],
  );
  const [showStationsModal, setStationsModal] = useState(false);

  const [quantity, setQuantity] = useState(1);

  useFocusEffect(
    useCallback(() => {
      const status_tela = STATUS_BY_TIPO_PRODUTO[localidade.tipo];
      if (status_tela) {
        rastrear({status_tela, cidade: localidade.cidade});
      }
    }, [localidade.tipo, localidade.cidade]),
  );

  useEffect(() => {
    async function fetchStationsByProductId() {
      await api
        .get('/listaPostosCompra', {
          params: {
            id_produto: id,
          },
        })
        .then(res => {
          setStationsByProductId(res.data);
        })
        .catch(error => {
          console.log(error);
        });
    }

    if (id) {
      fetchStationsByProductId();
    }
  }, [id]);

  function handlePayment() {
    if (isLogged) {
      if (localidade.tipo === 'V') {
        analytics().logEvent('add_vistoria_pre_cart', {
          id: 101,
          value: fuelPrice * quantity,
          currency: 'BRL',
          items: [
            {
              item_id: id,
              item_name: name,
              VALUE: fuelPrice * quantity,
              quantity: quantity,
            },
          ],
        });

        return navigation.navigate('PlacaVeiculo', {
          pedidos: {
            fuelId: id,
            imageUrl,
            liters: 1,
            name,
            quantity,
            value: fuelPrice * quantity,
            unidade,
          },
          localidade,
          posto,
        });
      }

      if (localidade.tipo === 'E') {
        analytics().logEvent('add_emplacamento_pre_cart', {
          id: 101,
          value: fuelPrice * quantity,
          currency: 'BRL',
          items: [
            {
              item_id: id,
              item_name: name,
              VALUE: fuelPrice * quantity,
              quantity: quantity,
            },
          ],
        });

        return navigation.navigate('AutEstampagem', {
          pedidos: {
            fuelId: id,
            imageUrl,
            liters: 1,
            name,
            quantity,
            value: fuelPrice * quantity,
            unidade,
          },
          localidade,
          posto,
        });
      }
      trackEvent('add_product_to_cart', {
        id: 101,
        value: fuelPrice * quantity,
        currency: 'BRL',
        items: [
          {
            item_id: id,
            item_name: name,
            value: fuelPrice * quantity,
            quantity: quantity,
          },
        ],
      });
      const paymentScreen = 'Payment';
      return navigation.navigate(paymentScreen, {
        pedidos: {
          fuelId: id,
          imageUrl,
          liters: 1,
          name,
          quantity,
          value: fuelPrice * quantity,
          unidade,
        },
        localidade,
        coupon,
        posto,
      });
    } else {
      openSign();
    }
  }

  function handleAddQuantity() {
    if (quantity < 5) {
      setQuantity(quantity + 1);
    }
  }

  function handleRemoveQuantity() {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  }

  return (
    <SafeAreaView edges={['bottom']} style={{flex: 1}}>
    <Container>
      <Header icon="chevron-left" />

      <Info>
        <IconContainer
          style={{
            shadowColor: theme.colors.text_dark,
            shadowOffset: {width: 0, height: 7},
            shadowOpacity: 0.2,
            shadowRadius: 6,
            elevation: 8,
          }}>
          <FuelImage source={{uri: imageUrl}} />
        </IconContainer>

        <ProductInfos>
          <Title>{name}</Title>
          <PriceInfo>
            <Price>{price}</Price>
            <Badge
              text={
                unidade.charAt(unidade.length - 1) === 's'
                  ? unidade.substring(0, unidade.length - 1)
                  : unidade
              }
              containerStyle={{
                backgroundColor: theme.colors.primary,
                borderColor: theme.colors.primary,
              }}
              textStyle={{color: theme.colors.shape}}
            />
          </PriceInfo>
        </ProductInfos>
      </Info>

      <Content
        contentContainerStyle={{paddingBottom: RFValue(20)}}
        showsVerticalScrollIndicator={false}>
        <SectionHeader style={{marginBottom: 0}}>
          {localidade.tipo !== 'V' && localidade.tipo !== 'E' && (
            <QuantityRow>
              <SectionTitle>Quantos cupons?</SectionTitle>

              <QuantityContainer>
                <QuantityButton
                  activeOpacity={1}
                  disabled={quantity <= 1}
                  style={{opacity: quantity <= 1 ? 0.4 : 1}}
                  onPress={handleRemoveQuantity}>
                  <QuantityIcon name="minus" />
                </QuantityButton>
                <Quantity>{quantity}</Quantity>
                <QuantityButton
                  activeOpacity={1}
                  disabled={quantity >= 5}
                  style={{opacity: quantity >= 5 ? 0.4 : 1}}
                  onPress={handleAddQuantity}>
                  <QuantityIcon name="plus" />
                </QuantityButton>
              </QuantityContainer>
            </QuantityRow>
          )}

          {(localidade.tipo === 'V' || localidade.tipo === 'E') && (
            <>
              <SectionDescription>
                Antes de prosseguir, é importante seguir os passos descritos
                abaixo de forma obrigatória. Isso garantirá uma experiência
                satisfatória e eficaz ao adquirir o produto/serviço.
              </SectionDescription>
            </>
          )}
        </SectionHeader>

        <Section>
          <SectionTitle>Local de retirada:</SectionTitle>
          <StationContainer
            activeOpacity={0.7}
            onPress={() => navigation.navigate('Station', {item: posto})}>
            <StationInfosContainer>
              <StationImage source={{uri: posto.flag_image}} />
              <StationInfos>
                <StationTitle numberOfLines={1} ellipsizeMode="tail">
                  {posto.name}
                </StationTitle>
                <StationDescription numberOfLines={1} ellipsizeMode="tail">
                  {posto.neighbourhood}
                </StationDescription>
              </StationInfos>
            </StationInfosContainer>

            <StationMapIcon name="chevron-right" />
          </StationContainer>
          {stationsByProductId.length > 0 && (
            <LinkButtonContainer>
              <LinkButton
                action={() => setStationsModal(true)}
                enabled
                text="ou também retire nestes locais"
              />
            </LinkButtonContainer>
          )}

          {alerta && alerta.length !== 0 && (
            <AlertContainer>
              <StepList
                containerStyle={{
                  backgroundColor: theme.colors.red,
                  marginBottom: 0,
                  marginTop: RFValue(12),
                }}
                textStyle={{
                  color: theme.colors.shape,
                  fontFamily: theme.fonts.semibold,
                }}
                text={alerta}
              />
            </AlertContainer>
          )}
        </Section>

        <Section>
          <SectionTitle>Como funciona:</SectionTitle>

          {localidade.tipo !== 'V' && localidade.tipo !== 'E' && (
            <List>
              <StepList number={1} text="Escolha a quantidade." />
              <StepList number={2} text="Realize o pagamento." />
              <StepList
                number={3}
                text="Dirija-se a loja para retirar o produto escolhido."
              />
              <StepList
                number={4}
                text="Ao chegar na loja, apresente o seu QR Code e pronto!"
              />
            </List>
          )}

          {localidade.tipo === 'V' && (
            <List>
              <StepList
                number={1}
                text="Entre no site ou app do Detran e clique no serviço desejado como: transferência de veículo, inclusão de gravame com vistoria ou alteração de característica."
              />
              <StepList
                number={2}
                text="Preencha os dados solicitados e faça a emissão da taxa e pagamento do boleto."
              />
              <StepList
                number={3}
                text="Após pagar a taxa, vá até a unidade de vistoria credenciada (não há necessidade de
                agendamento)."
              />
              <StepList
                number={4}
                text="Depois de realizada a inspeção veicular, compareça a uma unidade de atendimento
              do Detran, sem precisar agendar, para entrega da documentação referente ao serviço feito e do formulário de Termo de Responsabilidade/Serviços Veículos, disponível no site da autarquia (http://www.detran.df.gov.br/wp-content/uploads/2018/11/VE18-Termo_Responsabilid ade_Servicos_Veiculos.pdf), devidamente preenchido e assinado."
              />
              <StepList
                number={5}
                text="Agora só aguardar que os documentos poderão ser acessados pelo Portal de Serviços ou App do Detran."
              />
              <StepList
                number={6}
                text="Volte ao app Baratão, escolha o produto/serviço e clique em continuar."
              />
              <StepList
                number={7}
                text="Digite a placa do veículo a ser vistoriado."
              />
              <StepList number={8} text="Realize o pagamento." />
              <StepList
                number={9}
                text="Após realizar o pagamento, vá até uma unidade credenciada e apresente o cupom com QR Code."
              />
            </List>
          )}

          {localidade.tipo === 'E' && (
            <List>
              <StepList
                number={1}
                text="Entre no app ou site do Detran-DF na área de veículos e placa de identificação, solicite atendimento em uma das unidades do Detran e vá portando o documento do veículo (CRLV) e documento do proprietário do veículo."
              />
              <StepList
                number={2}
                text="Com a autorização de registro e o boleto emitido pelo Detran em mãos, faça o pagamento do boleto em uma agência bancária de um do(s) banco(s) indicado(s) no(s) boleto(s)."
              />
              <StepList
                number={3}
                text="Volte ao app Baratão, escolha o produto/serviço e clique em continuar."
              />
              <StepList
                number={4}
                text="Digite a autorização de estampagem emitida pelo Detran-DF."
              />
              <StepList number={5} text="Realize o pagamento." />
              <StepList
                number={6}
                text="Após realizar o pagamento, vá até uma unidade credenciada e apresente o cupom com QR Code."
              />
            </List>
          )}
        </Section>
      </Content>
      <Footer>

          <Button
            title={'Continuar'}
            enabled
            onPress={handlePayment}
          />

      </Footer>
      <StationsModal
        onDismiss={() => setStationsModal(false)}
        data={stationsByProductId}
        tipo={localidade.tipo}
        showModal={showStationsModal}
        showRank={false}
      />
    </Container>
    </SafeAreaView>
  );
}
